import { Icon, Number, Text } from "components/common"
import { financingType, leadsLostTypes } from "constants/list"
import { t } from "i18next"
import theme from "utils/themeVars"
import { saleTypeOptions } from "constants/list"
import { buildTreeKeyToRootColumnMap } from "utils/treeParentSelection"

export const renderAmount = amount => {
  return (
    <>
      <Number value={amount} compact="" digitAfterFraction={2} isCurrency />
      {" ("}
      <Number value={amount} />
      {")"}
    </>
  )
}

export const PIPELINE_CONFIG = () => ({
  prospect: {
    columnName: "prospect",
    title: t("Prospect"),
    backgroundColor: "#3655B3",
    tooltip: t("Qualified call completed; customer showed interest."),
  },
  matured: {
    columnName: "matured",
    title: t("Mature"),
    backgroundColor: "#0091BA",
    tooltip: t("Meetings held with the customer."),
  },
  preclosure: {
    columnName: "preclosure",
    title: t("Pre-Closure"),
    backgroundColor: "#1E1E1E",
    tooltip: t("Financial commitment made (Token)."),
  },
  sold: {
    columnName: "sold",
    title: t("Sold"),
    backgroundColor: "#009F2B",
    tooltip: t("Deal completed; customer has purchased the unit."),
    showUnitSold: true,
  },
  partialSold: {
    columnName: "partialSold",
    title: t("Partially Sold"),
    backgroundColor: "#388B91",
    tooltip: t("Financial commitment made (Partial Down Payment)."),
    showUnitSold: true,
  },
  wm_closed_won: {
    columnName: "wm_closed_won",
    title: t("Closed Won"),
    backgroundColor: "#679D62",
    tooltip: t("Deal completed; customer has purchased the unit."),
  },
  wm_execution: {
    columnName: "wm_execution",
    title: t("Execution"),
    backgroundColor: "#A09777",
    tooltip: t("Deal completed; customer has purchased the unit."),
  },

  wm_mature: {
    columnName: "wm_mature",
    title: t("Mature"),
    backgroundColor: "#0091BA",
    tooltip: t("Deal completed; customer has purchased the unit."),
  },
  wm_post_closure: {
    columnName: "wm_post_closure",
    title: t("Post Closure"),
    backgroundColor: "#4B5DBE",
    tooltip: t("Deal completed; customer has purchased the unit."),
  },
  wm_pre_closure: {
    columnName: "wm_pre_closure",
    title: t("Pre Closure"),
    backgroundColor: "#1E1E1E",
    tooltip: t("Deal completed; customer has purchased the unit."),
  },
  wm_partially_closed: {
    columnName: "wm_partially_closed",
    title: t("Partially Closed"),
    backgroundColor: "#BEB967",
    tooltip: t("Deal completed; customer has purchased the unit."),
  },
  wm_prospect: {
    columnName: "wm_prospect",
    title: t("Prospect"),
    backgroundColor: "#3655B3",
    tooltip: t("Deal completed; customer has purchased the unit."),
  },
})

export function orderedVisibleWealthPipelineStageKeys(stagesCsv, treeData = []) {
  const selected =
    typeof stagesCsv === "string"
      ? stagesCsv
          .split(",")
          .map(s => s.trim())
          .filter(Boolean)
      : Array.isArray(stagesCsv)
        ? [...stagesCsv]
        : []
  const pipelineConfig = PIPELINE_CONFIG()
  if (!selected.length) return []
  if (!treeData?.length) {
    return selected.filter(k => pipelineConfig[k])
  }
  const keyToRoot = buildTreeKeyToRootColumnMap(treeData)
  const columnKeys = [...new Set(selected.map(k => keyToRoot.get(k) ?? k))]
  const colSet = new Set(columnKeys)
  const ordered = []
  for (const node of treeData) {
    if (node?.key != null && colSet.has(node.key)) ordered.push(node.key)
  }
  const seen = new Set(ordered)
  columnKeys.filter(k => !seen.has(k)).forEach(k => ordered.push(k))
  return ordered.filter(k => pipelineConfig[k])
}

export const pipelineForcastedCards = () => ({
  currentPipeline: {
    title: t("Active Sales Pipeline"),
    icon: "GoPulse",
    accentColor: theme["accent-color"],
    tooltip: t("Total active pipeline (Sold, Partial Sold, PreClosure) before applying probabilities."),
  },
  forecastedPipeline: {
    title: t("Forcasted Pipeline"),
    icon: "MdOutlineShowChart",
    accentColor: theme["primary-color"],
    tooltip: t(
      "Projected pipeline after applying Manual and AI probabilities (Manual CP ≥ selected AND AI CP ≥ selected)."
    ),
  },
})

export const pipelineSummaryCards = () => ({
  totalPipeline: {
    title: t("Total Pipeline"),
    icon: "SvgIcPipeLine",
    accentColor: theme["accent-color"],
    descriptionKey: "totalRevenue",
    tooltip: t(
      "Combined value of all revenue across all stages, showing the total monetary volume currently in your pipeline."
    ),
    tooltipItems: [
      {
        key: "totalPotentialRevenue",
        title: t("Total Potential Revenue"),
      },
      {
        key: "totalRecognizedRevenue",
        title: t("Total Recognized Revenue"),
      },
      {
        key: "totalReportedRevenue",
        title: t("Total Reported Revenue"),
      },
    ],
  },

  sold: {
    title: t("Sold"),
    icon: "FaRegCheckCircle",
    accentColor: theme["accent-color"],
    descriptionKey: "totalSoldRevenue",
    tooltip: t("Total revenue in the Sold stage"),
    tooltipItems: [
      {
        key: "totalSoldPotentialRevenue",
        title: t("Total Potential Revenue"),
      },
      {
        key: "totalSoldRecognizedRevenue",
        title: t("Total Recognized Revenue"),
      },
      {
        key: "totalSoldReportedRevenue",
        title: t("Total Reported Revenue"),
      },
    ],
    unitsKey: "totalSoldUnits",
  },

  partialSold: {
    title: t("Partial Sold"),
    icon: "FiCheckCircle",
    accentColor: theme["accent-color"],
    descriptionKey: "totalPartialSoldRevenue",
    tooltip: t("Total revenue in the Partially Sold stage"),
    tooltipItems: [
      {
        key: "totalPartialSoldPotentialRevenue",
        title: t("Total Potential Revenue"),
      },
      {
        key: "totalPartialSoldRecognizedRevenue",
        title: t("Total Recognized Revenue"),
      },
      {
        key: "totalPartialSoldReportedRevenue",
        title: t("Total Reported Revenue"),
      },
    ],
    unitsKey: "totalPartialSoldUnits",
  },

  preclosure: {
    title: t("Pre-Closure"),
    icon: "FaCircleNotch",
    accentColor: theme["accent-color"],
    descriptionKey: "totalPreclosureRevenue",
    tooltip: t("Total revenue in the Pre-Closure stage"),
    tooltipItems: [
      {
        key: "totalPreclosurePotentialRevenue",
        title: t("Total Potential Revenue"),
      },
      {
        key: "totalPreclosureRecognizedRevenue",
        title: t("Total Recognized Revenue"),
      },
      {
        key: "totalPreclosureReportedRevenue",
        title: t("Total Reported Revenue"),
      },
    ],
  },

  lost: {
    title: t("Lost"),
    icon: "RxCrossCircled",
    accentColor: "red",
    descriptionKey: "totalLostRevenue",
    tooltip: t("Revenue that was once in pre-closure but dropped from the pipeline due to sale cancellations."),
  },
})

export const revenueMapping = {
  total: "totalRevenue",
  potential: "totalPotentialRevenue",
  recognized: "totalRecognizedRevenue",
  reported: "totalReportedRevenue",
  dealValue: "totalDealValue",
}

export const sortItems = () => [
  {
    label: t("Conversion Probability Ascending"),
    value: "Conversion Probability Ascending",
  },
  {
    label: t("Conversion Probability Descending"),
    value: "Conversion Probability Descending",
  },
  {
    label: t("AI Conversion Probability Ascending"),
    value: "AI Conversion Probability Ascending",
  },
  {
    label: t("AI Conversion Probability Descending"),
    value: "AI Conversion Probability Descending",
  },
  {
    label: t("Total Revenue Ascending"),
    value: "Total Revenue Ascending",
  },
  {
    label: t("Total Revenue Descending"),
    value: "Total Revenue Descending",
  },
  {
    label: t("Date Added Ascending"),
    value: "Date Added Ascending",
  },
  {
    label: t("Date Added Descending"),
    value: "Date Added Descending",
  },
]

const wealthPipelineSortItems = () => [
  {
    label: t("Deal Value Ascending"),
    value: "Deal Value Ascending",
  },
  {
    label: t("Deal Value Descending"),
    value: "Deal Value Descending",
  },
  {
    label: t("Date Added Ascending"),
    value: "Date Added Ascending",
  },
  {
    label: t("Date Added Descending"),
    value: "Date Added Descending",
  },
  {
    label: t("Conversion Probability Ascending"),
    value: "Conversion Probability Ascending",
  },
  {
    label: t("Conversion Probability Descending"),
    value: "Conversion Probability Descending",
  },
]

export const SORT_API_MAPPING = {
  "Conversion Probability Ascending": { sortBy: "conversionProbability", orderBy: "asc" },
  "Conversion Probability Descending": { sortBy: "conversionProbability", orderBy: "desc" },
  "AI Conversion Probability Ascending": { sortBy: "aiConversionProbability", orderBy: "asc" },
  "AI Conversion Probability Descending": { sortBy: "aiConversionProbability", orderBy: "desc" },
  "Total Revenue Ascending": { sortBy: "potentialRevenue", orderBy: "asc" },
  "Total Revenue Descending": { sortBy: "potentialRevenue", orderBy: "desc" },
  "Date Added Ascending": { sortBy: "createdAt", orderBy: "asc" },
  "Date Added Descending": { sortBy: "createdAt", orderBy: "desc" },
  "Deal Value Ascending": { sortBy: "dealValue", orderBy: "asc" },
  "Deal Value Descending": { sortBy: "dealValue", orderBy: "desc" },
}

export const filtersList = () => [
  {
    key: "range",
    filterLabel: t("Conversion Probability"),
    icon: "MdFilterList",
    popoverLabel: t("Conversion Probability"),
    appliedFilter: { type: "static-list" },
    filters: [
      {
        key: "conversionProbability",
        type: "slider",
        range: [0, 100],
        value: "0,100",
        label: t("Manual CP"),
        style: { width: 300 },
        setValue: v => v?.split(","),
        debounceDelay: 400,
      },
      {
        key: "aiConversionProbability",
        type: "slider",
        label: t("AI CP"),
        range: [0, 100],
        value: "0,100",
        style: { width: 300 },
        setValue: v => v?.split(","),
        debounceDelay: 400,
      },
    ],
  },
  {
    key: "saleType",
    filterLabel: t("Sale Type"),
    icon: "MdFilterList",
    type: "radio",
    popoverLabel: t("Sale Type"),
    fieldLabel: t("Sale Type"),
    options: saleTypeOptions(),
    appliedFilter: { type: "static-list" },
  },
  {
    key: "aiClosingMonth",
    filterLabel: t("AI Closing"),
    icon: "MdFilterList",
    type: "toggle",
    popoverLabel: t("AI Closing"),
    fieldLabel: t("AI Closing This Month"),
    inline: true,
    appliedFilter: { type: "toggle", text: t("AI Closing This Month") },
  },
  {
    key: "isLost",
    filterLabel: t("Leads"),
    icon: "MdFilterList",
    type: "radio",
    popoverLabel: t("Leads"),
    options: leadsLostTypes(),
    appliedFilter: { type: "static-list" },
  },
  {
    key: "unitSoldCounted",
    filterLabel: t("Units"),
    icon: "MdFilterList",
    type: "toggle",
    popoverLabel: t("Units Sold"),
    fieldLabel: t("Units Sold"),
    inline: true,
    appliedFilter: { type: "toggle", text: t("Units Sold") },
  },
  {
    key: "sort",
    filterLabel: t("Sort"),
    icon: "MdFilterList",
    type: "radio",
    popoverLabel: t("Sort"),
    options: sortItems(),
    appliedFilter: { type: "static-list" },
  },
]

export const wealthPipelineFiltersList = ({
  clientTypes,
  isFetchingClientTypes,
  capitalStructureOptions,
  isFetchingCapitalStructure,
  investmentStrategyOptions,
  investmentStrategyFetching,
  clientRatings,
  isFetchingClientRating,
  wealthPipelineStages,
  isFetchingWealthPipelineStages,
}) => [
  {
    key: "forecast",
    filterLabel: t("Forecast"),
    icon: "MdFilterList",
    popoverLabel: t("Conversion Probability"),
    appliedFilter: { type: "static-list" },
    filters: [
      {
        key: "conversionProbability",
        type: "slider",
        range: [0, 100],
        value: "0,100",
        label: t("Manual CP"),
        style: { width: 300 },
        setValue: v => v?.split(","),
        debounceDelay: 400,
      },
      {
        key: "dealValue",
        type: "slider",
        range: [0, 5000000000], // 5 billion
        value: "0,5000000000",
        label: t("Deal Value"),
        style: { width: 300 },
        setValue: v => v?.split(","),
        debounceDelay: 400,
      },
    ],
  },
  {
    filterLabel: t("Client Type"),
    popoverLabel: t("Client Type"),
    filterIcon: "MdFilterList",
    treeData: clientTypes,
    checkable: true,
    loading: isFetchingClientTypes,
    multiple: true,
    type: "tree",
    setValue: v => v?.split(","),
    key: "clientUserType",
    debounceDelay: 1000,
  },
  {
    key: "clientRatingId",
    popoverLabel: t("Client Rating"),
    filterLabel: t("Client Rating"),
    icon: "MdFilterList",
    type: "radio",
    loading: isFetchingClientRating,
    options: clientRatings,
  },
  {
    key: "investmentStrategy",
    popoverLabel: t("Investment Strategy"),
    filterLabel: t("Investment Strategy"),
    icon: "MdFilterList",
    type: "tree",
    loading: investmentStrategyFetching,
    treeData: investmentStrategyOptions,
    disabled: investmentStrategyFetching,
    checkable: true,
    multiple: true,
    debounceDelay: 1000,
    setValue: v => v?.split(","),
  },
  {
    key: "capitalStructure",
    popoverLabel: t("Capital Structure"),
    filterLabel: t("Capital Structure"),
    icon: "MdFilterList",
    loading: isFetchingCapitalStructure,
    type: "tree",
    treeData: capitalStructureOptions,
    disabled: isFetchingCapitalStructure,
    debounceDelay: 600,
    checkable: true,
    multiple: true,
    setValue: v => v?.split(","),
  },
  {
    key: "stages",
    popoverLabel: t("Stages"),
    filterLabel: t("Stages"),
    icon: "MdFilterList",
    checkable: true,
    multiple: true,
    setValue: v => v?.split(","),
    type: "tree",
    loading: isFetchingWealthPipelineStages,
    treeData: wealthPipelineStages,
    disabled: isFetchingWealthPipelineStages,
    debounceDelay: 600,
  },
  {
    key: "sort",
    filterLabel: t("Sort"),
    icon: "MdFilterList",
    type: "radio",
    popoverLabel: t("Sort"),
    options: wealthPipelineSortItems(),
    appliedFilter: { type: "static-list" },
  },
]

export const insightsFiltersList = () => [
  {
    key: "saleType",
    filterLabel: t("Sale Type"),
    icon: "MdFilterList",
    type: "radio",
    popoverLabel: t("Sale Type"),
    options: saleTypeOptions(),
    appliedFilter: { type: "static-list" },
  },
]

export const getTitle = (title, tooltip) => {
  return (
    <>
      <Text type="secondary" className="bold">
        {title}
      </Text>
      <Icon
        icon="BsInfo"
        tooltipText={tooltip}
        size="1.2em"
        color={theme["gray900"]}
        style={{
          backgroundColor: theme["gray400"],
          borderRadius: "1rem",
          "--icon-color": "#707070",
          marginLeft: "8",
        }}
      />
    </>
  )
}
